/**
 * Live-tag value normalizers.
 *
 * The proxy's `/live-data/tags` route is a thin upstream Ignition
 * payload pass-through. That payload is sometimes an array (one row
 * per requested tag) and sometimes a `path -> value` map. Normalize
 * both to a flat array keyed by `path` so renderers don't have to
 * branch.
 *
 * Pure functions, no RTK / no Ignition driver — trivially unit-testable.
 */

import { normalizeTagReadResults } from '@atbash-labs/ontology-core'

import type { PlantTagIdValue, PlantTagValue } from '../types'

/** Ignition's WebDev getTags returns `tagPath`. Older / OPC-shaped payloads may use `path` or `name` instead — accept all three. */
function pickPath(r: Record<string, unknown>): string | undefined {
  const candidates = ['tagPath', 'path', 'name'] as const
  for (const key of candidates) {
    const v = r[key]
    if (typeof v === 'string' && v.length > 0) return v
  }
  return undefined
}

function pickQuality(r: Record<string, unknown>): string | undefined {
  if (typeof r['quality'] === 'string') return r['quality'] as string
  // Some Ignition payloads only include `isGood: true/false`. Surface that
  // as a quality string so the renderer's quality chip lights up.
  if (typeof r['isGood'] === 'boolean') return r['isGood'] ? 'Good' : 'Bad'
  return undefined
}

function pickTimestamp(r: Record<string, unknown>): string | undefined {
  const t = r['timestamp']
  if (typeof t === 'string') return t
  if (typeof t === 'number' && Number.isFinite(t)) {
    return new Date(t).toISOString()
  }
  return undefined
}

export function normalizeLiveTagValues(raw: unknown): PlantTagValue[] {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw
      .map((item): PlantTagValue | null => {
        if (!item || typeof item !== 'object') return null
        const r = item as Record<string, unknown>
        const path = pickPath(r)
        if (!path) return null
        return {
          path,
          value: r['value'],
          quality: pickQuality(r),
          timestamp: pickTimestamp(r),
          unit: typeof r['unit'] === 'string' ? r['unit'] : undefined,
          error: typeof r['error'] === 'string' ? r['error'] : undefined
        }
      })
      .filter((t): t is PlantTagValue => t !== null)
  }
  if (typeof raw === 'object') {
    const result: PlantTagValue[] = []
    for (const [path, val] of Object.entries(raw as Record<string, unknown>)) {
      if (val && typeof val === 'object') {
        const r = val as Record<string, unknown>
        result.push({
          path,
          value: r['value'],
          quality: pickQuality(r),
          timestamp: pickTimestamp(r),
          unit: typeof r['unit'] === 'string' ? r['unit'] : undefined,
          error: typeof r['error'] === 'string' ? r['error'] : undefined
        })
      } else {
        result.push({ path, value: val })
      }
    }
    return result
  }
  return []
}

/** Normalize `POST /tags/read` result rows keyed by graph node ID. */
export function normalizeTagIdReadResults(raw: unknown): PlantTagIdValue[] {
  return normalizeTagReadResults(raw).map((row) => ({
    nodeId: row.nodeId,
    path: row.tagPath,
    value: row.value,
    quality: row.quality,
    timestamp: row.timestamp,
    stale: row.stale,
    unregistered: row.unregistered,
    ...(row.source ? { source: row.source } : {}),
    ...(row.errorCode ? { errorCode: row.errorCode } : {}),
    ...(row.warning ? { warning: row.warning } : {}),
    ...(row.isGood !== undefined ? { isGood: row.isGood } : {}),
    ...(row.warning ? { warning: row.warning } : {}),
    ...(row.error ? { error: row.error } : {})
  }))
}
